import React, { useState } from 'react';
import { X, Edit2, Save, Pause, Play, CheckCircle, AlertCircle } from 'lucide-react';
import Badge from '../../core/Badge/Badge';
import { MentoringRelationship, UpdateRelationshipData, EndRelationshipData } from '../../../types/admin';
import apiService from '../../../services/api';
import { formatDate } from '../../../utils/formatters';

interface RelationshipDetailModalProps {
  relationship: MentoringRelationship;
  onClose: () => void;
  onSuccess: () => void;
}

const RelationshipDetailModal: React.FC<RelationshipDetailModalProps> = ({
  relationship,
  onClose,
  onSuccess,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEndConfirm, setShowEndConfirm] = useState(false);

  const [formData, setFormData] = useState<UpdateRelationshipData>({
    status: relationship.status,
    expected_end_date: relationship.expected_end_date || '',
    relationship_goals: relationship.relationship_goals || '',
    admin_notes: relationship.admin_notes || '',
  });

  const [endData, setEndData] = useState<EndRelationshipData>({
    reason: '',
    mentee_rating: undefined,
    mentor_rating: undefined,
  });

  const handleInputChange = (field: keyof UpdateRelationshipData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      await apiService.admin.updateRelationship(relationship.id, formData);
      setIsEditing(false);
      onSuccess();
    } catch (err: any) {
      console.error('Error updating relationship:', err);
      setError(err.response?.data?.message || 'Error al actualizar la relación');
    } finally {
      setLoading(false);
    }
  };

  const handlePauseToggle = async () => {
    try {
      setLoading(true);
      setError(null);
      const newStatus = relationship.status === 'paused' ? 'active' : 'paused';
      await apiService.admin.updateRelationship(relationship.id, { status: newStatus });
      onSuccess();
    } catch (err: any) {
      console.error('Error toggling pause:', err);
      setError(err.response?.data?.message || 'Error al cambiar el estado');
    } finally {
      setLoading(false);
    }
  };

  const handleEnd = async () => {
    try {
      setLoading(true);
      setError(null);
      await apiService.admin.endRelationship(relationship.id, endData);
      setShowEndConfirm(false);
      onSuccess();
    } catch (err: any) {
      console.error('Error ending relationship:', err);
      setError(err.response?.data?.message || 'Error al finalizar la relación');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      active: { variant: 'success', label: 'Activa' },
      completed: { variant: 'default', label: 'Completada' },
      paused: { variant: 'warning', label: 'Pausada' },
      cancelled: { variant: 'danger', label: 'Cancelada' },
    };
    const config = variants[status as keyof typeof variants] || variants.active;
    return <Badge variant={config.variant as any}>{config.label}</Badge>;
  };

  const getDuration = (startDate: string, endDate?: string) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    const months = Math.floor(diffDays / 30);
    const days = diffDays % 30;
    return `${months} meses, ${days} días`;
  };

  if (showEndConfirm) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal--medium" onClick={(e) => e.stopPropagation()}>
          <div className="modal__header">
            <h2>Finalizar Relación de Mentoría</h2>
            <button className="modal__close" onClick={() => setShowEndConfirm(false)}>
              <X size={24} />
            </button>
          </div>

          <div className="modal__body">
            <div className="confirm-message">
              <AlertCircle size={48} className="confirm-icon" />
              <p>
                ¿Estás seguro de que deseas finalizar la relación entre{' '}
                <strong>{relationship.mentor_first_name} {relationship.mentor_last_name}</strong> y{' '}
                <strong>{relationship.mentee_first_name} {relationship.mentee_last_name}</strong>?
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Motivo de finalización</label>
              <textarea
                className="form-textarea"
                rows={3}
                value={endData.reason}
                onChange={(e) => setEndData({ ...endData, reason: e.target.value })}
                placeholder="Describe brevemente el motivo..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Rating del Mentee (opcional)</label>
                <input
                  type="number"
                  className="form-input"
                  min="1"
                  max="10"
                  value={endData.mentee_rating || ''}
                  onChange={(e) => setEndData({ ...endData, mentee_rating: e.target.value ? parseFloat(e.target.value) : undefined })}
                  placeholder="1-10"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Rating del Mentor (opcional)</label>
                <input
                  type="number"
                  className="form-input"
                  min="1"
                  max="10"
                  value={endData.mentor_rating || ''}
                  onChange={(e) => setEndData({ ...endData, mentor_rating: e.target.value ? parseFloat(e.target.value) : undefined })}
                  placeholder="1-10"
                />
              </div>
            </div>

            {error && (
              <div className="error-message">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="modal__footer">
            <button
              className="btn btn--outline"
              onClick={() => setShowEndConfirm(false)}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              className="btn btn--danger"
              onClick={handleEnd}
              disabled={loading}
            >
              {loading ? 'Finalizando...' : 'Finalizar Relación'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--large" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div>
            <h2>Detalle de Relación de Mentoría</h2>
            <div className="modal__meta">ID: {relationship.id}</div>
          </div>
          <button className="modal__close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal__body">
          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Status and Actions */}
          <div className="detail-header">
            <div className="detail-status">
              {getStatusBadge(isEditing ? formData.status! : relationship.status)}
            </div>
            <div className="detail-actions">
              {!isEditing ? (
                <>
                  <button
                    className="btn btn--outline btn--small"
                    onClick={() => setIsEditing(true)}
                    disabled={relationship.status === 'completed' || relationship.status === 'cancelled'}
                  >
                    <Edit2 size={16} />
                    <span>Editar</span>
                  </button>
                  {relationship.status === 'active' && (
                    <>
                      <button
                        className="btn btn--outline btn--small"
                        onClick={handlePauseToggle}
                        disabled={loading}
                      >
                        <Pause size={16} />
                        <span>Pausar</span>
                      </button>
                      <button
                        className="btn btn--danger btn--small"
                        onClick={() => setShowEndConfirm(true)}
                        disabled={loading}
                      >
                        <CheckCircle size={16} />
                        <span>Finalizar</span>
                      </button>
                    </>
                  )}
                  {relationship.status === 'paused' && (
                    <button
                      className="btn btn--primary btn--small"
                      onClick={handlePauseToggle}
                      disabled={loading}
                    >
                      <Play size={16} />
                      <span>Reanudar</span>
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    className="btn btn--outline btn--small"
                    onClick={() => setIsEditing(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn--primary btn--small"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    <Save size={16} />
                    <span>{loading ? 'Guardando...' : 'Guardar'}</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Mentor and Mentee Info */}
          <div className="detail-grid">
            <div className="detail-card">
              <h3 className="detail-card__title">Mentor</h3>
              <div className="person-info-large">
                <div className="person-name">
                  {relationship.mentor_first_name} {relationship.mentor_last_name}
                </div>
                {relationship.mentor_title && (
                  <div className="person-title">{relationship.mentor_title}</div>
                )}
                {relationship.mentor_company && (
                  <div className="person-company">{relationship.mentor_company}</div>
                )}
              </div>
            </div>

            <div className="detail-card">
              <h3 className="detail-card__title">Mentee</h3>
              <div className="person-info-large">
                <div className="person-name">
                  {relationship.mentee_first_name} {relationship.mentee_last_name}
                </div>
                {relationship.mentee_role && (
                  <div className="person-title">{relationship.mentee_role}</div>
                )}
                {relationship.mentee_company && (
                  <div className="person-company">{relationship.mentee_company}</div>
                )}
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="detail-section">
            <h3 className="detail-section__title">Fechas</h3>
            <div className="detail-grid detail-grid--3">
              <div className="detail-item">
                <div className="detail-item__label">Inicio</div>
                <div className="detail-item__value">{formatDate(relationship.start_date)}</div>
              </div>
              <div className="detail-item">
                <div className="detail-item__label">Duración</div>
                <div className="detail-item__value">
                  {getDuration(relationship.start_date, relationship.actual_end_date)}
                </div>
              </div>
              {relationship.actual_end_date && (
                <div className="detail-item">
                  <div className="detail-item__label">Finalización</div>
                  <div className="detail-item__value">{formatDate(relationship.actual_end_date)}</div>
                </div>
              )}
            </div>
          </div>

          {/* Goals */}
          <div className="detail-section">
            <h3 className="detail-section__title">Objetivos de la Relación</h3>
            {isEditing ? (
              <textarea
                className="form-textarea"
                rows={4}
                value={formData.relationship_goals}
                onChange={(e) => handleInputChange('relationship_goals', e.target.value)}
                placeholder="Describe los objetivos de esta relación de mentoría..."
              />
            ) : (
              <p className="detail-text">
                {relationship.relationship_goals || 'No se han definido objetivos específicos.'}
              </p>
            )}
          </div>

          {/* Admin Notes */}
          <div className="detail-section">
            <h3 className="detail-section__title">Notas del Administrador</h3>
            {isEditing ? (
              <textarea
                className="form-textarea"
                rows={3}
                value={formData.admin_notes}
                onChange={(e) => handleInputChange('admin_notes', e.target.value)}
                placeholder="Notas internas sobre esta relación..."
              />
            ) : (
              <p className="detail-text">
                {relationship.admin_notes || 'Sin notas adicionales.'}
              </p>
            )}
          </div>

          {/* Ratings */}
          {(relationship.mentee_satisfaction_rating || relationship.mentor_satisfaction_rating) && (
            <div className="detail-section">
              <h3 className="detail-section__title">Evaluaciones de Satisfacción</h3>
              <div className="detail-grid detail-grid--2">
                {relationship.mentee_satisfaction_rating && (
                  <div className="rating-card">
                    <div className="rating-card__label">Mentee</div>
                    <div className="rating-card__value">{relationship.mentee_satisfaction_rating}/10</div>
                  </div>
                )}
                {relationship.mentor_satisfaction_rating && (
                  <div className="rating-card">
                    <div className="rating-card__label">Mentor</div>
                    <div className="rating-card__value">{relationship.mentor_satisfaction_rating}/10</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Completion Reason */}
          {relationship.completion_reason && (
            <div className="detail-section">
              <h3 className="detail-section__title">Motivo de Finalización</h3>
              <p className="detail-text">{relationship.completion_reason}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RelationshipDetailModal;
